import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getUserByEmail, getAllUsers } from "./apis/getUser.service";
import { getBatchIssueByEmail } from "./apis/batchIssuing.service";

const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const token = localStorage.getItem("token");
  const hasToken = token && token !== "undefined" && token !== "null";

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // States for Get QR by Email
  const [qrList, setQrList] = useState([]);
  const [qrSearchEmail, setQrSearchEmail] = useState("");
  
  // Pagination & Search States for Get All Users
  const [usersPage, setUsersPage] = useState(1);
  const [usersLimit, setUsersLimit] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearchQuery, setUsersSearchQuery] = useState("");
  const [searchSource, setSearchSource] = useState("all_users");

  useEffect(() => {
    const publicPaths = ["/", "/signup"];
    if (!hasToken && !publicPaths.includes(currentPath)) {
      navigate("/");
    }
  }, [hasToken, currentPath, navigate]);

  const handleLogout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will be logged out of your session.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0b8a0b',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout!'
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        navigate("/");
      }
    });
  };

  const handleGetAllUsers = async (pageOverride, limitOverride, searchOverride) => {
    setModalLoading(true);
    setShowUsersModal(true);
    setSearchResult(null);
    
    const targetPage = pageOverride !== undefined ? pageOverride : usersPage;
    const targetLimit = limitOverride !== undefined ? limitOverride : usersLimit;
    const targetSearch = searchOverride !== undefined ? searchOverride : usersSearchQuery;

    try {
      const cleanToken = token?.replaceAll(/"/g, "") || "";
      const data = await getAllUsers({
        page: targetPage,
        limit: targetLimit,
        search: targetSearch
      }, cleanToken);

      setUsersList(data?.users || []);
      setUsersTotal(data?.total || 0);
      setUsersTotalPages(data?.totalPages || 1);

      if (pageOverride !== undefined) setUsersPage(pageOverride);
      if (limitOverride !== undefined) setUsersLimit(limitOverride);
      if (searchOverride !== undefined) setUsersSearchQuery(searchOverride);
    } catch (err) {
      console.error("Failed to get all users:", err);
      setUsersList([]);
      setUsersTotal(0);
      setUsersTotalPages(1);
    } finally {
      setModalLoading(false);
    }
  };

  const handleSearchUser = async () => {
    if (!searchEmail) return;
    setModalLoading(true);
    setSearchResult(null);
    setUsersList([]);
    setShowUsersModal(true);
    try {
      const cleanToken = token?.replaceAll(/"/g, "") || "";
      const res = await getUserByEmail(searchEmail, cleanToken);
      console.log("getUserByEmail Response:", res);

      let foundUser = null;
      if (res) {
        if (res.userID || res.userName || res.email) {
          foundUser = res;
        } else if (res.data && (res.data.userID || res.data.userName)) {
          foundUser = res.data;
        } else if (res.user && (res.user.userID || res.user.userName)) {
          foundUser = res.user;
        }
      }

      if (foundUser) {
        setSearchResult(foundUser);
      } else {
        setSearchResult("NOT_FOUND");
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        setSearchResult("NOT_FOUND");
      } else {
        setSearchResult("ERROR");
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleGetQrByEmail = async () => {
    if (!qrSearchEmail) return;
    setModalLoading(true);
    setQrList([]);
    try {
      const cleanToken = token?.replaceAll(/"/g, "") || "";
      const records = await getBatchIssueByEmail(qrSearchEmail, cleanToken);
      console.log("getBatchIssueByEmail Response:", records);
      
      if (Array.isArray(records)) {
        setQrList(records);
      } else {
        setQrList([]);
      }
    } catch (err) {
      console.error(err);
      setQrList([]);
      Swal.fire({
        icon: 'error',
        title: 'Query Failed',
        text: err.message || 'An error occurred while fetching QR details.',
        confirmButtonColor: '#0b8a0b'
      });
    } finally {
      setModalLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      icon: 'success',
      title: `${type} copied!`,
      text: 'The text has been copied to your clipboard.',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const navItemStyle = (path) => {
    const isActive = currentPath === path;
    return {
      textDecoration: "none",
      fontSize: "14px",
      fontWeight: isActive ? "600" : "500",
      color: "#ffffff",
      backgroundColor: isActive ? "rgba(255, 255, 255, 0.18)" : "transparent",
      padding: "8px 16px",
      borderRadius: "6px",
      transition: "all 0.2s ease-in-out",
      display: "inline-block",
      cursor: "pointer"
    };
  };

  const hoverEffectClass = `
    .nav-link-custom:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      transform: translateY(-1px);
    }
    .nav-link-custom:active {
      transform: translateY(0);
    }
    .hamburger-menu {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 24px;
      height: 18px;
      cursor: pointer;
      z-index: 1001;
      transition: all 0.2s;
    }
    .hamburger-menu span {
      display: block;
      width: 100%;
      height: 2.5px;
      background-color: #ffffff;
      border-radius: 2px;
      transition: all 0.3s ease;
    }
    .hamburger-menu:hover span {
      background-color: rgba(255,255,255,0.8);
    }
    .drawer-action-btn:hover {
      background-color: rgba(11, 138, 11, 0.08) !important;
      transform: translateY(-1px);
    }
    .drawer-action-btn:active {
      transform: translateY(0);
    }
    .drawer-logout-btn:hover {
      background-color: #c9302c !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(217, 83, 79, 0.2);
    }
    .drawer-logout-btn:active {
      transform: translateY(0);
    }
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    .no-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `;

  return (
    <>
      <style>{hoverEffectClass}</style>
      <div 
        style={{ 
          height: "64px", 
          background: "linear-gradient(135deg, #0b8a0b 0%, #086b08 100%)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backdropFilter: "blur(8px)"
        }} 
        className="d-flex align-items-center justify-content-between px-4 py-2"
      >
        {/* LOGO */}
        <div 
          onClick={() => navigate(hasToken ? "/batchissuing" : "/")} 
          style={{ cursor: "pointer", transition: "transform 0.2s" }}
          className="d-flex align-items-center nav-link-custom"
        >
          <img 
            height={32} 
            src="https://mygreenlight.biz/assets/images/logo-white.png" 
            alt="Greenlight Logo" 
            style={{ display: "block" }}
          />
        </div>

        {/* NAVIGATION LINKS */}
        <div className="d-flex align-items-center gap-3">
          {hasToken ? (
            <>
              <div
                onClick={() => navigate("/batchissuing")}
                style={navItemStyle("/batchissuing")}
                className="nav-link-custom"
              >
                Batch Issuing
              </div>

              <div
                onClick={() => navigate("/generateoffer")}
                style={navItemStyle("/generateoffer")}
                className="nav-link-custom"
              >
                Generate Offer
              </div>

              <div
                onClick={() => navigate("/createuser")}
                style={navItemStyle("/createuser")}
                className="nav-link-custom"
              >
                Create User
              </div>

              {/* Hamburger 3-line Menu */}
              <div 
                onClick={() => setIsDrawerOpen(true)}
                className="hamburger-menu ms-3"
              >
                <span></span>
                <span></span>
                <span></span>
              </div>
            </>
          ) : (
            <>
              <Link 
                to="/" 
                style={navItemStyle("/")}
                className="nav-link-custom"
              >
                Login
              </Link>
              <Link 
                to="/signup" 
                style={navItemStyle("/signup")}
                className="nav-link-custom"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Drawer Overlay Backdrop */}
      {isDrawerOpen && (
        <div 
          onClick={() => setIsDrawerOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 1005,
            backdropFilter: "blur(2px)",
            transition: "all 0.3s ease"
          }}
        />
      )}

      {/* Sliding Drawer Menu */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "320px",
          backgroundColor: "#ffffff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          zIndex: 1006,
          transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Drawer Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h5 style={{ margin: 0, fontWeight: "700", color: "#333" }}>Admin Panel</h5>
          <button 
            onClick={() => setIsDrawerOpen(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#aaa",
              lineHeight: 1
            }}
          >
            &times;
          </button>
        </div>

        <hr style={{ borderTop: "1px solid #eee", margin: "0 0 24px 0" }} />

        {/* Drawer Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", flex: 1 }}>
          
           {/* Option 1: Get All Users */}
          <div>
            <button
              onClick={() => {
                setIsDrawerOpen(false);
                setShowSearchForm(false);
                setSearchSource("all_users");
                setUsersPage(1);
                setUsersLimit(10);
                setUsersSearchQuery("");
                handleGetAllUsers(1, 10, "");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                backgroundColor: "transparent",
                color: "#0b8a0b",
                border: "1px solid #0b8a0b",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              className="drawer-action-btn"
            >
              <i className="fa-solid fa-users me-2"></i> Get All Users
            </button>
          </div>

          {/* Option 2: Get User by Email */}
          <div>
            <button
              onClick={() => {
                setIsDrawerOpen(false);
                setShowSearchForm(true);
                setSearchSource("search_email");
                setSearchResult(null);
                setUsersList([]);
                setSearchEmail("");
                setShowUsersModal(true);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                backgroundColor: "transparent",
                color: "#0b8a0b",
                border: "1px solid #0b8a0b",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              className="drawer-action-btn"
            >
              <i className="fa-solid fa-user me-2"></i> Get User by Email
            </button>
          </div>

          {/* Option 3: Get QR by Email */}
          <div>
            <button
              onClick={() => {
                setIsDrawerOpen(false);
                setShowSearchForm(true);
                setSearchSource("get_qr");
                setSearchResult(null);
                setUsersList([]);
                setQrList([]);
                setQrSearchEmail("");
                setShowUsersModal(true);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                backgroundColor: "transparent",
                color: "#0b8a0b",
                border: "1px solid #0b8a0b",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              className="drawer-action-btn"
            >
              <i className="fa-solid fa-qrcode me-2"></i> Get QR by Email
            </button>
          </div>

        </div>

        {/* Option 4: Logout */}
        <div style={{ marginTop: "auto" }}>
          <button
            onClick={() => {
              setIsDrawerOpen(false);
              handleLogout();
            }}
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#d9534f",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            className="drawer-logout-btn"
          >
            <i className="fa-solid fa-right-from-bracket me-2"></i> Logout
          </button>
        </div>
      </div>

      {/* Users & Search Result Modal */}
      {showUsersModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1100,
          backdropFilter: "blur(4px)"
        }}>
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "950px",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "16px 24px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#fcfcfc"
            }}>
              <h5 style={{ margin: 0, fontWeight: "600", color: "#333" }}>
                {searchSource === "get_qr" ? "Get QR Details" : searchResult ? "User Profile Details" : showSearchForm ? "Get User by Email" : "All Users"}
              </h5>
              <button 
                onClick={() => {
                  setShowUsersModal(false);
                  setSearchResult(null);
                  setUsersList([]);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#aaa"
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="no-scrollbar" style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {modalLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                  <div className="spinner-border" style={{ color: "#0b8a0b" }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : searchSource === "get_qr" ? (
                <div>
                  {/* Search Form */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "10px 0", marginBottom: "20px", borderBottom: qrList.length > 0 ? "1px solid #eee" : "none" }}>
                    <label style={{ fontSize: "14px", fontWeight: "600", color: "#555" }}>Enter User Email to Retrieve QR Details</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <input
                        type="email"
                        placeholder="demo@example.com"
                        value={qrSearchEmail}
                        onChange={(e) => setQrSearchEmail(e.target.value)}
                        className="form-control"
                        style={{
                          padding: "10px 14px",
                          borderRadius: "6px",
                          fontSize: "14px"
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleGetQrByEmail();
                        }}
                      />
                      <button
                        onClick={handleGetQrByEmail}
                        className="btn"
                        style={{
                          backgroundColor: "#0b8a0b",
                          color: "#ffffff",
                          fontWeight: "600",
                          padding: "10px 24px",
                          borderRadius: "6px"
                        }}
                      >
                        Search
                      </button>
                    </div>
                  </div>

                  {qrList.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                      <p className="text-muted" style={{ fontSize: "13px" }}>Found {qrList.length} batch issuing record(s):</p>
                      
                      {qrList.map((qr, idx) => (
                        <div 
                          key={idx} 
                          style={{
                            border: "1px solid #e8e8e8",
                            borderRadius: "10px",
                            backgroundColor: "#fdfdfd",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            overflow: "hidden"
                          }}
                        >
                          {/* Card Header */}
                          <div style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e8e8e8", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: "700", color: "#333", fontSize: "14px" }}>
                              <i className="fa-solid fa-envelope me-2" style={{ color: "#0b8a0b" }}></i>{qr.email}
                            </span>
                            <span className="text-muted" style={{ fontSize: "12px" }}>
                              <i className="fa-solid fa-calendar-days me-1"></i> {new Date(qr.createdAt).toLocaleString()}
                            </span>
                          </div>

                          {/* Card Body */}
                          <div style={{ padding: "20px" }}>
                            <div className="row g-4 align-items-center">
                              {/* Left column: QR code display */}
                              <div className="col-12 col-md-4 text-center">
                                <div style={{ 
                                  border: "2px solid #0b8a0b", 
                                  borderRadius: "8px", 
                                  padding: "8px", 
                                  display: "inline-block", 
                                  backgroundColor: "#fff",
                                  boxShadow: "0 2px 6px rgba(11, 138, 11, 0.1)"
                                }}>
                                  <img 
                                    src={qr.qrCodeUrl} 
                                    alt="QR Code" 
                                    style={{ width: "140px", height: "140px", objectFit: "contain", display: "block" }} 
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "https://placehold.co/140x140?text=QR+Code";
                                    }}
                                  />
                                </div>
                                <div className="mt-2">
                                  <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>
                                    Vendor User ID: {qr.vendorUserId || "Unknown"}
                                  </span>
                                </div>
                              </div>

                              {/* Right column: Action Links */}
                              <div className="col-12 col-md-8">
                                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                                  
                                  {/* S3 URL Section */}
                                  <div>
                                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#555", display: "block", marginBottom: "4px" }}>
                                      S3 QR Code URL
                                    </label>
                                    <div className="input-group">
                                      <input 
                                        type="text" 
                                        readOnly 
                                        value={qr.qrCodeUrl} 
                                        className="form-control form-control-sm text-secondary" 
                                        style={{ fontSize: "12px", backgroundColor: "#f5f5f5" }}
                                      />
                                      <button 
                                        className="btn btn-sm btn-outline-success" 
                                        type="button"
                                        onClick={() => copyToClipboard(qr.qrCodeUrl, "S3 URL")}
                                        title="Copy S3 URL"
                                      >
                                        <i className="fa-solid fa-copy"></i>
                                      </button>
                                      <a 
                                        href={qr.qrCodeUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="btn btn-sm btn-outline-success"
                                        title="Open Link"
                                      >
                                        <i className="fa-solid fa-external-link"></i>
                                      </a>
                                    </div>
                                  </div>

                                  {/* Deeplink Section */}
                                  <div>
                                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#555", display: "block", marginBottom: "4px" }}>
                                      Deeplink URI
                                    </label>
                                    <div className="input-group">
                                      <input 
                                        type="text" 
                                        readOnly 
                                        value={qr.deeplink} 
                                        className="form-control form-control-sm text-secondary" 
                                        style={{ fontSize: "12px", backgroundColor: "#f5f5f5" }}
                                      />
                                      <button 
                                        className="btn btn-sm btn-outline-success" 
                                        type="button"
                                        onClick={() => copyToClipboard(qr.deeplink, "Deeplink")}
                                        title="Copy Deeplink"
                                      >
                                        <i className="fa-solid fa-copy"></i>
                                      </button>
                                    </div>
                                  </div>

                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    qrSearchEmail && !modalLoading && (
                      <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        <i className="fa-solid fa-circle-info me-2 text-warning" style={{ fontSize: "18px" }}></i>
                        No QR or batch issue records found for this email address.
                      </div>
                    )
                  )}

                  {qrList.length === 0 && !qrSearchEmail && (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                      <i className="fa-solid fa-search me-2 text-secondary" style={{ fontSize: "24px", display: "block", marginBottom: "10px" }}></i>
                      Enter an email address above to view the corresponding generated QR codes, S3 links, and deeplinks.
                    </div>
                  )}
                </div>
              ) : showSearchForm && !searchResult ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "10px 0" }}>
                  <label style={{ fontSize: "14px", fontWeight: "600", color: "#555" }}>Enter User Email or ID</label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input
                      type="email"
                      placeholder="user@example.com"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      className="form-control"
                      style={{
                        padding: "10px 14px",
                        borderRadius: "6px",
                        fontSize: "14px"
                      }}
                    />
                    <button
                      onClick={handleSearchUser}
                      className="btn"
                      style={{
                        backgroundColor: "#0b8a0b",
                        color: "#ffffff",
                        fontWeight: "600",
                        padding: "10px 20px",
                        borderRadius: "6px"
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>
              ) : searchResult ? (
                <div>
                  {searchResult === "NOT_FOUND" ? (
                    <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
                      User not found.
                    </div>
                  ) : searchResult === "ERROR" ? (
                    <div style={{ textAlign: "center", color: "#d9534f", padding: "20px" }}>
                      An error occurred during search.
                    </div>
                  ) : (
                    <div className="container-fluid py-2">
                      <div className="row g-4">
                        {/* Left Side: Avatar and Quick Stats */}
                        <div className="col-12 col-md-4 text-center d-flex flex-column align-items-center justify-content-center" style={{ borderRight: "1px solid #eee" }}>
                          <div style={{ marginBottom: "16px" }}>
                            <img 
                              src={`${process.env.PUBLIC_URL || ""}/profile_avatar.png`} 
                              alt="Profile" 
                              style={{ width: "96px", height: "96px", borderRadius: "50%", objectFit: "cover", border: "3px solid #0b8a0b", padding: "3px", backgroundColor: "#fff" }} 
                            />
                          </div>
                          <h4 style={{ margin: "0 0 4px 0", fontWeight: "700", color: "#333" }}>
                            {searchResult.firstName || ""} {searchResult.middleName ? searchResult.middleName + " " : ""}{searchResult.lastName || ""}
                          </h4>
                          <p className="text-muted" style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
                            @{searchResult.username || searchResult.userName || "username"}
                          </p>
                          
                          <div className="d-flex flex-wrap gap-2 justify-content-center">
                            <span 
                              style={{ 
                                fontSize: "12px", 
                                fontWeight: "600", 
                                padding: "4px 10px", 
                                borderRadius: "12px",
                                backgroundColor: searchResult.status === "active" ? "#d1e7dd" : "#f8d7da",
                                color: searchResult.status === "active" ? "#0f5132" : "#842029"
                              }}
                            >
                              {searchResult.status || "inactive"}
                            </span>
                            <span 
                              style={{ 
                                fontSize: "12px", 
                                fontWeight: "600", 
                                padding: "4px 10px", 
                                borderRadius: "12px",
                                backgroundColor: "#0b8a0b",
                                color: "#ffffff"
                              }}
                            >
                              {searchResult.userType || "user"}
                            </span>
                          </div>
                        </div>

                        {/* Right Side: Detailed Info Sections */}
                        <div className="col-12 col-md-8">
                          <div style={{ paddingRight: "8px" }}>
                            
                            {/* Section 1: Contact & Profile */}
                            <h6 style={{ color: "#0b8a0b", fontWeight: "700", fontSize: "13px", borderBottom: "1px solid #eee", paddingBottom: "4px", marginBottom: "12px" }}>
                              Contact & Profile
                            </h6>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "18px", fontSize: "13px" }}>
                              <div className="d-flex justify-content-between"><span className="text-muted">First Name</span><span className="fw-semibold text-dark">{searchResult.firstName || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Middle Name</span><span className="fw-semibold text-dark">{searchResult.middleName || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Last Name</span><span className="fw-semibold text-dark">{searchResult.lastName || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Email</span><span className="fw-semibold text-dark">{searchResult.email || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Phone No</span><span className="fw-semibold text-dark">{searchResult.phoneNo || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Gender</span><span className="fw-semibold text-dark">{searchResult.gender || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Date of Birth</span><span className="fw-semibold text-dark">{searchResult.dateOfBirth || "N/A"}</span></div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Profile Pic URL</span>
                                <span className="fw-semibold text-dark text-break text-end" style={{ maxWidth: "240px" }}>
                                  {searchResult.profilePic ? (
                                    <a href={searchResult.profilePic} target="_blank" rel="noopener noreferrer" style={{ color: "#0b8a0b", textDecoration: "underline" }}>
                                      {searchResult.profilePic}
                                    </a>
                                  ) : "N/A"}
                                </span>
                              </div>
                            </div>

                            {/* Section 2: Account & Security */}
                            <h6 style={{ color: "#0b8a0b", fontWeight: "700", fontSize: "13px", borderBottom: "1px solid #eee", paddingBottom: "4px", marginBottom: "12px" }}>
                              Account & Security
                            </h6>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "18px", fontSize: "13px" }}>
                              <div className="d-flex justify-content-between"><span className="text-muted">Database ID</span><span className="fw-semibold text-dark">{searchResult._id || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">User ID</span><span className="fw-semibold text-dark">{searchResult.id || searchResult.userID || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Username</span><span className="fw-semibold text-dark">{searchResult.username || searchResult.userName || "N/A"}</span></div>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Password (Hash)</span>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fw-semibold text-dark" style={{ fontFamily: showPassword ? "monospace" : "inherit" }}>
                                    {showPassword ? (searchResult.jhiPassword || "N/A") : "••••••••"}
                                  </span>
                                  {searchResult.jhiPassword && (
                                    <button 
                                      onClick={() => setShowPassword(!showPassword)}
                                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#0b8a0b", display: "flex", alignItems: "center" }}
                                      title={showPassword ? "Hide password" : "Show password"}
                                    >
                                      <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ fontSize: "12px" }}></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Login Provider</span><span className="fw-semibold text-dark">{searchResult.loginProvider || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">User Type</span><span className="fw-semibold text-dark">{searchResult.userType || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Status</span><span className="fw-semibold text-dark">{searchResult.status || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Two-Factor Auth</span><span className="fw-semibold text-dark">{searchResult.twoFactorAuth || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Vendor ID</span><span className="fw-semibold text-dark">{searchResult.vendor_Id || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Address ID</span><span className="fw-semibold text-dark">{searchResult.addressId || "N/A"}</span></div>
                            </div>

                            {/* Section 3: Metadata */}
                            <h6 style={{ color: "#0b8a0b", fontWeight: "700", fontSize: "13px", borderBottom: "1px solid #eee", paddingBottom: "4px", marginBottom: "12px" }}>
                              System Metadata
                            </h6>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px" }}>
                              <div className="d-flex justify-content-between"><span className="text-muted">Created Date</span><span className="fw-semibold text-dark">{searchResult.createdDate || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Last Change Date</span><span className="fw-semibold text-dark">{searchResult.lastChangeDate || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Created User</span><span className="fw-semibold text-dark">{searchResult.createdUser || "N/A"}</span></div>
                              <div className="d-flex justify-content-between"><span className="text-muted">Last Modified User</span><span className="fw-semibold text-dark">{searchResult.lastModifiedUser || "N/A"}</span></div>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                    {searchSource === "all_users" ? (
                      <button
                        onClick={() => {
                          setSearchResult(null);
                        }}
                        className="btn btn-sm"
                        style={{ color: "#0b8a0b", borderColor: "#0b8a0b", fontWeight: "600" }}
                      >
                        <i className="fa-solid fa-arrow-left me-1"></i> Back to List
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSearchResult(null);
                          setSearchEmail("");
                        }}
                        className="btn btn-sm"
                        style={{ color: "#0b8a0b", borderColor: "#0b8a0b", fontWeight: "600" }}
                      >
                        Search Another User
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {/* Top Search & Filter Bar */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                    {/* Search Input */}
                    <div style={{ display: "flex", gap: "8px", flex: 1, maxWidth: "300px" }}>
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={usersSearchQuery}
                        onChange={(e) => setUsersSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleGetAllUsers(1, usersLimit, usersSearchQuery);
                          }
                        }}
                        className="form-control form-control-sm"
                        style={{ borderRadius: "6px" }}
                      />
                      <button
                        onClick={() => handleGetAllUsers(1, usersLimit, usersSearchQuery)}
                        className="btn btn-sm"
                        style={{ backgroundColor: "#0b8a0b", color: "#ffffff", fontWeight: "600", borderRadius: "6px" }}
                      >
                        Search
                      </button>
                    </div>

                    {/* Limit Dropdown */}
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: "13px", color: "#555" }}>Limit:</span>
                      <select
                        value={usersLimit}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setUsersLimit(val);
                          handleGetAllUsers(1, val, usersSearchQuery);
                        }}
                        className="form-select form-select-sm"
                        style={{ width: "80px", borderRadius: "6px" }}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>

                  {usersList.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped table-bordered align-middle" style={{ fontSize: "13px" }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "50px" }}>#</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Full Name</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th style={{ width: "80px" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((user, idx) => {
                            const globalIdx = (usersPage - 1) * usersLimit + idx + 1;
                            const name = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
                            return (
                              <tr key={idx}>
                                <td>{globalIdx}</td>
                                <td className="fw-semibold">{user.username || user.userName || "N/A"}</td>
                                <td>{user.email || "N/A"}</td>
                                <td>{name}</td>
                                <td>
                                  <span className="badge" style={{ backgroundColor: user.userType === "admin" ? "#0b8a0b" : "#6c757d" }}>
                                    {user.userType || "user"}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${user.status === "active" ? "bg-success" : "bg-danger"}`}>
                                    {user.status || "inactive"}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    onClick={() => {
                                      setSearchResult(user);
                                      setShowPassword(false);
                                    }}
                                    className="btn btn-sm btn-outline-success"
                                    style={{ padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", color: "#666", padding: "40px 20px" }}>
                      No users found.
                    </div>
                  )}

                  {/* Pagination Controls */}
                  {usersList.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3 pt-2" style={{ borderTop: "1px solid #eee" }}>
                      <div style={{ fontSize: "13px", color: "#666" }}>
                        Showing {Math.min((usersPage - 1) * usersLimit + 1, usersTotal)} to {Math.min(usersPage * usersLimit, usersTotal)} of {usersTotal} entries
                      </div>
                      
                      <div className="d-flex align-items-center gap-2">
                        <button
                          onClick={() => {
                            if (usersPage > 1) {
                              handleGetAllUsers(usersPage - 1, usersLimit, usersSearchQuery);
                            }
                          }}
                          disabled={usersPage <= 1}
                          className="btn btn-sm btn-outline-success"
                          style={{ padding: "4px 12px", borderRadius: "6px", fontWeight: "600", cursor: usersPage <= 1 ? "not-allowed" : "pointer" }}
                        >
                          Previous
                        </button>
                        
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#333", margin: "0 8px" }}>
                          Page {usersPage} of {usersTotalPages}
                        </span>
                        
                        <button
                          onClick={() => {
                            if (usersPage < usersTotalPages) {
                              handleGetAllUsers(usersPage + 1, usersLimit, usersSearchQuery);
                            }
                          }}
                          disabled={usersPage >= usersTotalPages}
                          className="btn btn-sm btn-outline-success"
                          style={{ padding: "4px 12px", borderRadius: "6px", fontWeight: "600", cursor: usersPage >= usersTotalPages ? "not-allowed" : "pointer" }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: "16px 24px",
              borderTop: "1px solid #eee",
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: "#fcfcfc"
            }}>
              <button 
                onClick={() => {
                  setShowUsersModal(false);
                  setSearchResult(null);
                  setUsersList([]);
                }}
                className="btn"
                style={{ backgroundColor: "#0b8a0b", color: "white", padding: "8px 20px", fontWeight: "600", borderRadius: "6px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Nav;
